import { useState } from "react";
import {
  useRelationshipLevels,
  useUpdateRelationshipLevel,
  useUpdateTop30,
} from "@/hooks/queries";
import { RelationshipLevelTable } from "@components/RelationshipLevelTable";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Popover from "@mui/material/Popover";
import styles from "./styles.module.css";

type Props = {
  profileId: string;
  currentLevel: number | null | undefined;
  currentTop30?: boolean | null;
  onLevelChanged?: () => void;
};

export const RelationshipLevelSelect = ({
  profileId,
  currentLevel,
  currentTop30,
  onLevelChanged,
}: Props) => {
  const { data: levels = [] } = useRelationshipLevels();
  const updateLevel = useUpdateRelationshipLevel();
  const updateTop30 = useUpdateTop30();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const currentLevelData = levels.find((l) => l.level === currentLevel);

  const applyLevel = (newLevel: number | null) => {
    updateLevel.mutate(
      { profileId, level: newLevel },
      { onSuccess: () => onLevelChanged?.() }
    );
  };

  const handleSelect = (newLevel: number | null) => {
    setAnchorEl(null);
    if (newLevel === currentLevel) return;
    // Clearing the level on a Top 30 contact also removes them from Top 30 — confirm first.
    if (newLevel === null && currentTop30) {
      setConfirmClearOpen(true);
      return;
    }
    applyLevel(newLevel);
  };

  const handleConfirmClear = async () => {
    setConfirmClearOpen(false);
    // Invariant-safe order: drop Top 30 first, then clear the level.
    await updateTop30.mutateAsync({ profileId, top30: false });
    await updateLevel.mutateAsync({ profileId, level: null });
    onLevelChanged?.();
  };

  const open = Boolean(anchorEl);
  const isPending = updateLevel.isPending || updateTop30.isPending;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={isPending}
        className={styles.triggerButton}
      >
        {currentLevelData
          ? `${currentLevelData.level} - ${currentLevelData.title}`
          : <span className={styles.placeholder}>Set level…</span>}
      </Button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <RelationshipLevelTable currentLevel={currentLevel} onSelect={handleSelect} />
      </Popover>
      <Dialog open={confirmClearOpen} onClose={() => setConfirmClearOpen(false)}>
        <DialogTitle>Remove from Top 30?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are clearing the Relationship level on a Top 30 contact. This will
            also remove them from your top 30, are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClearOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmClear} color="error" variant="contained">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
